import type { ProjectDiscussion } from '@/features/publicsite/types/community/ProjectDiscussion';
import type { DiscussionState } from '@/features/publicsite/types/research/DiscussionState';
import type { PublicCatalogKey } from '@/i18n/dictionaries/publicCatalog';

/**
 * The URL of one topic inside a forum. Two segments because a topic is addressed through its
 * forum (`/comunitate/forumuri/:forumId/subiecte/:topicId`), and the forum part is a slug while
 * the topic part is an id — which is why this is not forumPath with something appended.
 */
export function topicPath(forumSlug: string, topicId: string): string {
  return `/comunitate/forumuri/${forumSlug}/subiecte/${topicId}`;
}

/** The five things the discussion block can say — see discussionState for each. */
export const DISCUSSION_STATE = {
  loading: 'loading',
  start: 'start',
  signin: 'signin',
  open: 'open',
  failed: 'failed',
} as const;

/**
 * What the block should say, given what came back and who is reading — extracted so the four
 * states are testable, since this project's Vitest has no DOM and a rendered component cannot be
 * asserted on at all.
 *
 * The states exist because "no discussion yet" must never render as an empty container. A blank
 * box under a heading reads as a broken page; worse, a link into a thread with nothing in it
 * reads as an abandoned conversation and stops the next reader from starting one. So:
 *
 *  - `loading`   — the request is still out. Says nothing rather than guessing.
 *  - `start`     — nobody has started one and the reader is signed in: an invitation, with a box.
 *  - `signin`    — nobody has started one and the reader is anonymous: an invitation to sign in.
 *  - `open`      — there is a conversation. The link, and how much is in it.
 *  - `failed`    — the read was refused, so which of the above applies is unknown. Says so.
 */
export function discussionState(
  discussion: ProjectDiscussion | null,
  loaded: boolean,
  signedIn: boolean,
  failed = false,
): DiscussionState {
  if (!loaded) return DISCUSSION_STATE.loading;
  if (discussion) return DISCUSSION_STATE.open;
  // Checked before the two invitations: a refused read cannot tell an existing conversation from
  // an absent one, and claiming "nobody has started one" would hide a thread that is there.
  if (failed) return DISCUSSION_STATE.failed;
  return signedIn ? DISCUSSION_STATE.start : DISCUSSION_STATE.signin;
}

/**
 * How to describe a conversation that exists — the count, or the fact that it is with a
 * moderator. Zero approved replies has two meanings and the reader deserves the right one.
 */
export function discussionSummaryKey(discussion: ProjectDiscussion): PublicCatalogKey {
  if (discussion.awaitingModeration) return 'discussionAwaitingModeration';
  return discussion.replies === 0 ? 'discussionNoRepliesYet' : 'discussionReplies';
}
